import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Filter, 
  Search, 
  Calendar, 
  Tag, 
  Users, 
  MessageSquare, 
  Bot, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Target, 
  Activity,
  X,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { cn } from '../../../utils/cn';

interface ConversationFilters {
  status: string[];
  priority: string[];
  channel: string[];
  campaign: string[];
  assignee: string[];
  dateRange: { start: Date | null; end: Date | null };
  sentiment: string[];
  requiresAttention: boolean;
  aiHandled: boolean;
  humanReviewRequired: boolean;
}

interface EnhancedConversation {
  id: string;
  status: string;
  priority: string;
  channel?: string;
  campaign?: string;
  aiAnalysis?: {
    sentiment: string;
    confidence: number;
  };
  metadata: {
    processedBy?: string;
  };
}

interface ConversationFiltersProps {
  filters: ConversationFilters;
  conversations: EnhancedConversation[];
  onFiltersChange: (filters: ConversationFilters) => void;
}

interface QuickFilter {
  label: string;
  count: number;
  color: string;
  filterKey: keyof ConversationFilters;
  filterValue: any;
}

const FilterCategory: React.FC<{
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ title, icon: Icon, children, isExpanded, onToggle }) => {
  return (
    <div className="border border-neutral-700/50 rounded-lg bg-neutral-800/30">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-neutral-700/30 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Icon className="w-4 h-4 text-neutral-400" />
          <span className="text-white text-sm font-medium">{title}</span>
        </div>
        <ChevronDown className={cn(
          "w-4 h-4 text-neutral-400 transition-transform duration-200",
          isExpanded && "rotate-180"
        )} />
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CheckboxFilter: React.FC<{
  options: Array<{ value: string; label: string; count?: number; icon?: React.ElementType }>;
  selected: string[];
  onChange: (selected: string[]) => void;
  multiSelect?: boolean;
}> = ({ options, selected, onChange, multiSelect = true }) => {
  const handleChange = (value: string) => {
    if (multiSelect) {
      const newSelected = selected.includes(value)
        ? selected.filter(s => s !== value)
        : [...selected, value];
      onChange(newSelected);
    } else {
      onChange(selected.includes(value) ? [] : [value]);
    }
  };

  return (
    <div className="space-y-2 mt-2">
      {options.map((option) => {
        const isSelected = selected.includes(option.value);
        const OptionIcon = option.icon;
        
        return (
          <label
            key={option.value}
            className="flex items-center space-x-2 cursor-pointer group"
          >
            <div className={cn(
              "w-4 h-4 border-2 rounded flex items-center justify-center transition-colors",
              isSelected
                ? "bg-blue-500 border-blue-500"
                : "border-neutral-600 group-hover:border-neutral-500"
            )}>
              {isSelected && (
                <CheckCircle className="w-2 h-2 text-white" />
              )}
            </div>
            <div className="flex items-center space-x-2 flex-1">
              {OptionIcon && <OptionIcon className="w-3 h-3 text-neutral-400" />}
              <span className={cn(
                "text-sm",
                isSelected ? "text-white" : "text-neutral-300"
              )}>
                {option.label}
              </span>
              {option.count !== undefined && (
                <span className="text-xs text-neutral-500 ml-auto">
                  {option.count}
                </span>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
};

const ToggleFilter: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon?: React.ElementType;
  description?: string;
}> = ({ label, checked, onChange, icon: Icon, description }) => {
  return (
    <label className="flex items-center justify-between cursor-pointer group py-2">
      <div className="flex items-center space-x-2">
        {Icon && <Icon className="w-4 h-4 text-neutral-400" />}
        <div>
          <span className={cn(
            "text-sm",
            checked ? "text-white" : "text-neutral-300"
          )}>
            {label}
          </span>
          {description && (
            <p className="text-xs text-neutral-500">{description}</p>
          )}
        </div>
      </div>
      <div className={cn(
        "w-10 h-6 rounded-full border-2 transition-colors relative",
        checked
          ? "bg-blue-500 border-blue-500"
          : "bg-neutral-700 border-neutral-600"
      )}>
        <div className={cn(
          "w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform",
          checked ? "translate-x-4" : "translate-x-0.5"
        )} />
      </div>
    </label>
  );
};

export const ConversationFilters: React.FC<ConversationFiltersProps> = ({
  filters,
  conversations,
  onFiltersChange
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['status', 'priority']));
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);

  // Calculate filter counts
  const getFilterCounts = () => {
    const statusCounts = conversations.reduce((acc, conv) => {
      acc[conv.status] = (acc[conv.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const priorityCounts = conversations.reduce((acc, conv) => {
      acc[conv.priority] = (acc[conv.priority] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const channelCounts = conversations.reduce((acc, conv) => {
      if (conv.channel) {
        acc[conv.channel] = (acc[conv.channel] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const sentimentCounts = conversations.reduce((acc, conv) => {
      if (conv.aiAnalysis?.sentiment) {
        acc[conv.aiAnalysis.sentiment] = (acc[conv.aiAnalysis.sentiment] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return { statusCounts, priorityCounts, channelCounts, sentimentCounts };
  };

  const { statusCounts, priorityCounts, channelCounts, sentimentCounts } = getFilterCounts();

  // Quick filters
  const quickFilters: QuickFilter[] = [
    {
      label: 'Needs Attention',
      count: conversations.filter(c => c.status === 'needs-response' || c.status === 'high-priority').length,
      color: 'bg-red-500',
      filterKey: 'requiresAttention',
      filterValue: true
    },
    {
      label: 'AI Handled',
      count: conversations.filter(c => c.metadata.processedBy === 'ai').length,
      color: 'bg-cyan-500',
      filterKey: 'aiHandled',
      filterValue: true
    },
    {
      label: 'Human Review',
      count: conversations.filter(c => c.status === 'needs-response').length,
      color: 'bg-yellow-500',
      filterKey: 'humanReviewRequired',
      filterValue: true
    },
    {
      label: 'Escalated',
      count: conversations.filter(c => c.status === 'escalated').length,
      color: 'bg-purple-500',
      filterKey: 'status',
      filterValue: ['escalated']
    }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', count: statusCounts.active, icon: Activity },
    { value: 'positive', label: 'Positive', count: statusCounts.positive, icon: CheckCircle },
    { value: 'high-priority', label: 'High Priority', count: statusCounts['high-priority'], icon: AlertCircle },
    { value: 'needs-response', label: 'Needs Response', count: statusCounts['needs-response'], icon: Clock },
    { value: 'escalated', label: 'Escalated', count: statusCounts.escalated, icon: Users },
    { value: 'closed', label: 'Closed', count: statusCounts.closed, icon: CheckCircle }
  ].filter(option => option.count > 0);

  const priorityOptions = [
    { value: 'critical', label: 'Critical', count: priorityCounts.critical, icon: AlertCircle },
    { value: 'high', label: 'High', count: priorityCounts.high, icon: AlertCircle },
    { value: 'medium', label: 'Medium', count: priorityCounts.medium, icon: Clock },
    { value: 'low', label: 'Low', count: priorityCounts.low, icon: CheckCircle }
  ].filter(option => option.count > 0);

  const channelOptions = Object.entries(channelCounts).map(([channel, count]) => ({
    value: channel,
    label: channel.toUpperCase(),
    count,
    icon: MessageSquare
  }));

  const sentimentOptions = [
    { value: 'positive', label: 'Positive', count: sentimentCounts.positive, icon: CheckCircle },
    { value: 'neutral', label: 'Neutral', count: sentimentCounts.neutral, icon: Clock },
    { value: 'negative', label: 'Negative', count: sentimentCounts.negative, icon: AlertCircle }
  ].filter(option => option.count > 0);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const updateFilters = (key: keyof ConversationFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      status: [],
      priority: [],
      channel: [],
      campaign: [],
      assignee: [],
      dateRange: { start: null, end: null },
      sentiment: [],
      requiresAttention: false,
      aiHandled: false,
      humanReviewRequired: false
    });
  };

  const getActiveFilterCount = () => {
    return Object.entries(filters).reduce((count, [key, value]) => {
      if (key === 'dateRange') {
        return count + (value.start || value.end ? 1 : 0);
      }
      if (Array.isArray(value)) {
        return count + value.length;
      }
      if (typeof value === 'boolean' && value) {
        return count + 1;
      }
      return count;
    }, 0);
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-neutral-400" />
          <span className="text-white font-medium">Filters</span>
          {activeFilterCount > 0 && (
            <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllFilters}
            className="border-neutral-600 text-neutral-300 hover:bg-red-500/20"
          >
            <X className="w-3 h-3 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Quick Filters */}
      <div>
        <p className="text-neutral-400 text-xs mb-2 font-medium">Quick Filters</p>
        <div className="grid grid-cols-2 gap-2">
          {quickFilters.map((filter, index) => (
            <button
              key={index}
              onClick={() => {
                if (filter.filterKey === 'status') {
                  updateFilters('status', filter.filterValue);
                } else {
                  updateFilters(filter.filterKey, filter.filterValue);
                }
              }}
              className={cn(
                "p-2 rounded-lg border transition-all duration-200 text-left",
                (filter.filterKey === 'requiresAttention' && filters.requiresAttention) ||
                (filter.filterKey === 'aiHandled' && filters.aiHandled) ||
                (filter.filterKey === 'humanReviewRequired' && filters.humanReviewRequired) ||
                (filter.filterKey === 'status' && JSON.stringify(filters.status) === JSON.stringify(filter.filterValue))
                  ? "border-blue-500/50 bg-blue-500/10"
                  : "border-neutral-700/50 bg-neutral-800/30 hover:bg-neutral-800/50"
              )}
            >
              <div className="flex items-center justify-between">
                <span className="text-white text-xs font-medium">{filter.label}</span>
                <span className="text-neutral-400 text-xs">{filter.count}</span>
              </div>
              <div className={cn("w-full h-1 rounded-full mt-1", filter.color)} />
            </button>
          ))}
        </div>
      </div>

      {/* Status Filter */}
      <FilterCategory
        title="Status"
        icon={Activity}
        isExpanded={expandedSections.has('status')}
        onToggle={() => toggleSection('status')}
      >
        <CheckboxFilter
          options={statusOptions}
          selected={filters.status}
          onChange={(selected) => updateFilters('status', selected)}
        />
      </FilterCategory>

      {/* Priority Filter */}
      <FilterCategory
        title="Priority"
        icon={AlertCircle}
        isExpanded={expandedSections.has('priority')}
        onToggle={() => toggleSection('priority')}
      >
        <CheckboxFilter
          options={priorityOptions}
          selected={filters.priority}
          onChange={(selected) => updateFilters('priority', selected)}
        />
      </FilterCategory>

      {/* Channel Filter */}
      {channelOptions.length > 0 && (
        <FilterCategory
          title="Channel"
          icon={MessageSquare}
          isExpanded={expandedSections.has('channel')}
          onToggle={() => toggleSection('channel')}
        >
          <CheckboxFilter
            options={channelOptions}
            selected={filters.channel}
            onChange={(selected) => updateFilters('channel', selected)}
          />
        </FilterCategory>
      )}

      {/* Sentiment Filter */}
      {sentimentOptions.length > 0 && (
        <FilterCategory
          title="Sentiment"
          icon={Target}
          isExpanded={expandedSections.has('sentiment')}
          onToggle={() => toggleSection('sentiment')}
        >
          <CheckboxFilter
            options={sentimentOptions}
            selected={filters.sentiment}
            onChange={(selected) => updateFilters('sentiment', selected)}
          />
        </FilterCategory>
      )}

      {/* AI Filters */}
      <FilterCategory
        title="AI Processing"
        icon={Bot}
        isExpanded={expandedSections.has('ai')}
        onToggle={() => toggleSection('ai')}
      >
        <div className="space-y-2">
          <ToggleFilter
            label="AI Handled"
            checked={filters.aiHandled}
            onChange={(checked) => updateFilters('aiHandled', checked)}
            icon={Bot}
            description="Conversations processed by AI"
          />
          <ToggleFilter
            label="Human Review Required"
            checked={filters.humanReviewRequired}
            onChange={(checked) => updateFilters('humanReviewRequired', checked)}
            icon={Users}
            description="Flagged for human attention"
          />
          <ToggleFilter
            label="Requires Attention"
            checked={filters.requiresAttention}
            onChange={(checked) => updateFilters('requiresAttention', checked)}
            icon={AlertCircle}
            description="High priority or escalated"
          />
        </div>
      </FilterCategory>

      {/* Date Range Filter */}
      <FilterCategory
        title="Date Range"
        icon={Calendar}
        isExpanded={expandedSections.has('date')}
        onToggle={() => toggleSection('date')}
      >
        <div className="space-y-2 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-neutral-400 text-xs mb-1 block">From</label>
              <input
                type="date"
                value={filters.dateRange.start?.toISOString().split('T')[0] || ''}
                onChange={(e) => updateFilters('dateRange', {
                  ...filters.dateRange,
                  start: e.target.value ? new Date(e.target.value) : null
                })}
                className="w-full px-2 py-1 bg-neutral-800/50 border border-neutral-700 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-neutral-400 text-xs mb-1 block">To</label>
              <input
                type="date"
                value={filters.dateRange.end?.toISOString().split('T')[0] || ''}
                onChange={(e) => updateFilters('dateRange', {
                  ...filters.dateRange,
                  end: e.target.value ? new Date(e.target.value) : null
                })}
                className="w-full px-2 py-1 bg-neutral-800/50 border border-neutral-700 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Quick date ranges */}
          <div className="grid grid-cols-2 gap-1 mt-2">
            {[
              { label: 'Today', days: 0 },
              { label: 'Yesterday', days: 1 },
              { label: 'Last 7 days', days: 7 },
              { label: 'Last 30 days', days: 30 }
            ].map((range) => (
              <button
                key={range.label}
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - range.days);
                  updateFilters('dateRange', { start, end });
                }}
                className="px-2 py-1 bg-neutral-800/50 hover:bg-neutral-700/50 border border-neutral-700 rounded text-white text-xs transition-colors"
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </FilterCategory>

      {/* Applied Filters Summary */}
      {activeFilterCount > 0 && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-300 text-sm font-medium">Applied Filters</span>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/20"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Reset
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-1">
            {filters.status.map(status => (
              <span key={status} className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                {status}
              </span>
            ))}
            {filters.priority.map(priority => (
              <span key={priority} className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                {priority}
              </span>
            ))}
            {filters.channel.map(channel => (
              <span key={channel} className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded-full">
                {channel}
              </span>
            ))}
            {filters.sentiment.map(sentiment => (
              <span key={sentiment} className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs rounded-full">
                {sentiment}
              </span>
            ))}
            {filters.requiresAttention && (
              <span className="px-2 py-1 bg-red-500/20 text-red-300 text-xs rounded-full">
                Needs Attention
              </span>
            )}
            {filters.aiHandled && (
              <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 text-xs rounded-full">
                AI Handled
              </span>
            )}
            {filters.humanReviewRequired && (
              <span className="px-2 py-1 bg-orange-500/20 text-orange-300 text-xs rounded-full">
                Human Review
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 